import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AlertCircle, AlertTriangle, ArrowDown, ArrowRight, ArrowUp, ChevronDown, FlaskConical, Pencil, Plus, Route, Save, Star, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { useDiscardGuard } from '../../components/DiscardGuard'
import ConditionEditor, { describeCondition } from '../../components/ConditionEditor'
import FormRenderer from '../../components/FormRenderer'
import { WorkflowTimeline, type TimelineStep } from '../../components/WorkflowTimeline'
import { platformApi } from '../../api'
import { activePages } from '../../lib/form'
import type { Answers } from '../../types'
import { useAdmin } from './AdminData'
import { PersonPicker, type PickedPerson } from './PersonPicker'
import { moveItem, nextKey, removeAt, replaceAt, toKeyed, toPayload, toTimelineSteps, type KeyedRule } from './model'

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong')

// ------------------------------------------------------------ rule editor

function RuleSheet({ rule, open, onOpenChange, onApply }: {
    rule: KeyedRule | null
    open: boolean
    onOpenChange: (o: boolean) => void
    onApply: (r: KeyedRule) => void
}) {
    const admin = useAdmin()
    const [draft, setDraft] = useState<KeyedRule | null>(rule)
    const [error, setError] = useState('')

    const [initial, setInitial] = useState('')
    useEffect(() => { if (open) { setDraft(rule); setInitial(JSON.stringify(rule)); setError('') } }, [open, rule])
    const guard = useDiscardGuard(draft !== null && JSON.stringify(draft) !== initial, onOpenChange)
    if (!draft) return null

    const workflows = admin.workflows.filter((w) => !w.archived || w.id === draft.workflow_id)
    const apply = () => {
        if (!draft.name.trim()) { setError('Give the rule a name'); return }
        if (!draft.workflow_id) { setError('Choose the workflow this rule routes to'); return }
        onApply({ ...draft, name: draft.name.trim() })
        onOpenChange(false)
    }

    return (
        <Sheet open={open} onOpenChange={guard.requestClose}>
            <SheetContent className="w-full data-[side=right]:sm:max-w-2xl" {...guard.contentProps}>
                <SheetHeader>
                    <SheetTitle>{rule?.name ? `Edit ${rule.name}` : 'New routing rule'}</SheetTitle>
                    <SheetDescription>The first active rule that matches a submission picks its workflow.</SheetDescription>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto px-4">
                    <FieldGroup>
                        {error && <Alert variant="destructive"><AlertCircle /><AlertTitle>{error}</AlertTitle></Alert>}
                        <Field>
                            <FieldLabel htmlFor="rule-name">Name</FieldLabel>
                            <Input id="rule-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                        </Field>
                        <Field>
                            <FieldLabel>When</FieldLabel>
                            <ConditionEditor value={draft.condition} fields={admin.routingFields} emptyText="Every submission"
                                onChange={(c) => setDraft({ ...draft, condition: c ?? { all: [] } })} />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="rule-wf">Route to workflow</FieldLabel>
                            <Select value={draft.workflow_id || undefined} onValueChange={(v) => setDraft({ ...draft, workflow_id: v })}>
                                <SelectTrigger id="rule-wf" className="w-full sm:w-72"><SelectValue placeholder="Choose a workflow…" /></SelectTrigger>
                                <SelectContent><SelectGroup>
                                    {workflows.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}{w.archived ? ' (archived)' : ''}</SelectItem>)}
                                </SelectGroup></SelectContent>
                            </Select>
                        </Field>
                        <Field orientation="horizontal">
                            <Switch id="rule-active" checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
                            <FieldLabel htmlFor="rule-active">Active</FieldLabel>
                        </Field>
                    </FieldGroup>
                </div>
                <SheetFooter className="border-t sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" onClick={() => guard.requestClose(false)}>Cancel</Button>
                    <Button type="button" onClick={apply}>Apply rule</Button>
                </SheetFooter>
            </SheetContent>
            {guard.dialog}
        </Sheet>
    )
}

// ------------------------------------------------------------ test panel

function TestPanel() {
    const admin = useAdmin()
    const [slug, setSlug] = useState('')
    const [submitter, setSubmitter] = useState<PickedPerson | null>(null)
    const [answers, setAnswers] = useState<Answers>({})
    const [running, setRunning] = useState(false)
    const [result, setResult] = useState<{ workflow?: string; steps: TimelineStep[] } | null>(null)
    const [error, setError] = useState('')

    const formDef = admin.forms.find((f) => f.form.slug === slug)?.definition ?? null
    const pages = formDef ? activePages(formDef, answers) : []

    const run = async () => {
        setError(''); setResult(null); setRunning(true)
        try {
            const r = await platformApi.testRouting(slug, answers, submitter!.id)
            if (r.error) setError(r.error)
            else setResult({ workflow: r.workflow_name, steps: toTimelineSteps((r.steps ?? []).map((s) => ({ ...s, assignees: s.assignees ?? [] }))) })
        } catch (e) {
            setError(errMsg(e))
        } finally {
            setRunning(false)
        }
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><FlaskConical className="size-4" /> Test routing</CardTitle>
                <CardDescription>See which workflow and which people a submission would resolve to. Nothing is saved.</CardDescription>
            </CardHeader>
            <CardContent>
                <FieldGroup>
                    <Field>
                        <FieldLabel htmlFor="test-form">Form</FieldLabel>
                        <Select value={slug || undefined} onValueChange={(v) => { setSlug(v); setAnswers({}); setResult(null) }}>
                            <SelectTrigger id="test-form" className="w-full sm:w-72"><SelectValue placeholder="Choose a form…" /></SelectTrigger>
                            <SelectContent><SelectGroup>
                                {admin.forms.map((f) => <SelectItem key={f.form.slug} value={f.form.slug}>{f.form.name}</SelectItem>)}
                            </SelectGroup></SelectContent>
                        </Select>
                    </Field>
                    <Field>
                        <FieldLabel>Submitter</FieldLabel>
                        <PersonPicker value={submitter} onSelect={setSubmitter} placeholder="Choose a submitter…" />
                    </Field>
                    {pages.length > 0 && (
                        <Collapsible className="rounded-md border">
                            <CollapsibleTrigger asChild>
                                <Button type="button" variant="ghost" className="w-full justify-between">
                                    Sample answers (optional) <ChevronDown data-icon="inline-end" />
                                </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="flex flex-col gap-6 p-4">
                                {pages.map((p) => (
                                    <section key={p.id} className="flex flex-col gap-3">
                                        <h4 className="text-sm font-semibold">{p.title}</h4>
                                        <FormRenderer page={p} answers={answers} onChange={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))} />
                                    </section>
                                ))}
                            </CollapsibleContent>
                        </Collapsible>
                    )}
                    <div>
                        <Button type="button" disabled={!slug || !submitter || running} onClick={run}>
                            {running ? <Spinner data-icon="inline-start" /> : <FlaskConical data-icon="inline-start" />} Test
                        </Button>
                    </div>
                    {error && (
                        <Alert variant="destructive"><AlertCircle /><AlertTitle>Could not resolve a workflow</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>
                    )}
                    {result && (
                        <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-4">
                            <div className="text-sm">Routes to <Badge>{result.workflow ?? 'Unknown workflow'}</Badge></div>
                            <WorkflowTimeline steps={result.steps} orientation="vertical" />
                        </div>
                    )}
                </FieldGroup>
            </CardContent>
        </Card>
    )
}

// ------------------------------------------------------------ tab

export default function RoutingTab() {
    const admin = useAdmin()
    const [rules, setRules] = useState<KeyedRule[]>([])
    const [loaded, setLoaded] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState('')
    const [editing, setEditing] = useState<KeyedRule | null>(null)
    const [sheetOpen, setSheetOpen] = useState(false)

    const load = useCallback(async () => {
        try {
            setRules(toKeyed(await platformApi.getRouting()))
            setDirty(false)
            setLoadError('')
        } catch (e) {
            setLoadError(errMsg(e))
        } finally {
            setLoaded(true)
        }
    }, [])
    useEffect(() => { void load() }, [load])

    const wfName = useMemo(() => new Map(admin.workflows.map((w) => [w.id, w.name])), [admin.workflows])
    const defaultWf = admin.workflows.find((w) => w.is_default && !w.archived)

    const change = (next: KeyedRule[]) => { setRules(next); setDirty(true) }
    const apply = (r: KeyedRule) => {
        const i = rules.findIndex((x) => x._key === r._key)
        change(i < 0 ? [...rules, r] : replaceAt(rules, i, r))
    }
    const openNew = () => {
        setEditing({ _key: nextKey('r'), name: '', condition: { all: [] }, workflow_id: defaultWf?.id ?? '', active: true })
        setSheetOpen(true)
    }

    const save = async () => {
        setSaving(true); setSaveError('')
        try {
            setRules(toKeyed(await platformApi.putRouting(toPayload(rules))))
            setDirty(false)
            toast.success('Routing saved')
        } catch (e) {
            setSaveError(errMsg(e))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <Card className={defaultWf ? undefined : 'border-destructive'}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Star className="size-4" /> Default workflow</CardTitle>
                    <CardDescription>Catches every submission that no rule below matches.</CardDescription>
                </CardHeader>
                <CardContent>
                    {defaultWf ? (
                        <div className="flex flex-wrap items-center gap-3">
                            <Badge>{defaultWf.name}</Badge>
                            <Button type="button" variant="link" size="sm" onClick={() => admin.goTab('workflows')}>View in Workflows</Button>
                        </div>
                    ) : (
                        <Alert variant="destructive">
                            <AlertTriangle />
                            <AlertTitle>No default workflow</AlertTitle>
                            <AlertDescription>
                                Submissions that match no rule will fail to route.{' '}
                                <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => admin.goTab('workflows')}>Choose a default workflow</Button>
                            </AlertDescription>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <section className="flex flex-col gap-3" aria-labelledby="rules-heading">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h3 id="rules-heading" className="text-sm font-semibold">Routing rules</h3>
                        <p className="text-sm text-muted-foreground">Evaluated top to bottom. The first match wins.</p>
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={openNew} disabled={admin.workflows.length === 0}>
                            <Plus data-icon="inline-start" /> Add rule
                        </Button>
                        <Button type="button" onClick={save} disabled={!dirty || saving}>
                            {saving ? <Spinner data-icon="inline-start" /> : <Save data-icon="inline-start" />} Save order &amp; rules
                        </Button>
                    </div>
                </div>

                {saveError && <Alert variant="destructive"><AlertCircle /><AlertTitle>Could not save routing</AlertTitle><AlertDescription>{saveError}</AlertDescription></Alert>}
                {loadError && <Alert variant="destructive"><AlertCircle /><AlertTitle>Could not load routing</AlertTitle><AlertDescription>{loadError}</AlertDescription></Alert>}
                {dirty && <p className="text-xs text-muted-foreground">You have unsaved changes.</p>}

                {!loaded ? (
                    <Skeleton className="h-28" />
                ) : rules.length === 0 ? (
                    <Empty className="border">
                        <EmptyHeader>
                            <EmptyMedia variant="icon"><Route /></EmptyMedia>
                            <EmptyTitle>No routing rules</EmptyTitle>
                            <EmptyDescription>Everything goes to the default workflow. Add a rule to route by form or answer.</EmptyDescription>
                        </EmptyHeader>
                    </Empty>
                ) : (
                    <ol className="flex flex-col gap-3">
                        {rules.map((r, i) => (
                            <li key={r._key}>
                                <Card size="sm" className={r.active ? undefined : 'opacity-60'}>
                                    <CardContent className="flex flex-wrap items-center gap-3">
                                        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary" aria-label={`Position ${i + 1}`}>{i + 1}</span>
                                        <div className="min-w-0 flex-1 basis-64">
                                            <div className="truncate text-sm font-medium">{r.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                When {describeCondition(r.condition, admin.routingFields).toLowerCase() === 'always' ? 'any submission arrives' : describeCondition(r.condition, admin.routingFields)}
                                            </div>
                                        </div>
                                        <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
                                        <Badge variant={wfName.has(r.workflow_id) ? 'secondary' : 'destructive'}>{wfName.get(r.workflow_id) ?? 'Missing workflow'}</Badge>
                                        <Switch checked={r.active} aria-label={`${r.name} active`} onCheckedChange={(v) => change(replaceAt(rules, i, { ...r, active: v }))} />
                                        <div className="flex gap-1">
                                            <Button type="button" variant="ghost" size="icon-sm" disabled={i === 0} aria-label={`Move ${r.name} up`} onClick={() => change(moveItem(rules, i, i - 1))}><ArrowUp /></Button>
                                            <Button type="button" variant="ghost" size="icon-sm" disabled={i === rules.length - 1} aria-label={`Move ${r.name} down`} onClick={() => change(moveItem(rules, i, i + 1))}><ArrowDown /></Button>
                                            <Button type="button" variant="ghost" size="icon-sm" aria-label={`Edit ${r.name}`} onClick={() => { setEditing(r); setSheetOpen(true) }}><Pencil /></Button>
                                            <Button type="button" variant="ghost" size="icon-sm" aria-label={`Delete ${r.name}`} onClick={() => change(removeAt(rules, i))}><Trash2 /></Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </li>
                        ))}
                    </ol>
                )}
            </section>

            <TestPanel />
            <RuleSheet rule={editing} open={sheetOpen} onOpenChange={setSheetOpen} onApply={apply} />
        </div>
    )
}
